import { useState } from 'react';
import { formatMoney, type Bid } from '@teentack/shared';
import { EmptyState } from '@/components/ui/Feedback';
import { Skeleton } from '@/components/ui/Skeleton';
import { ReputationBadge } from '@/features/reviews/Stars';
import { useReputations } from '@/features/reviews/use-reviews';
import { MessageButton } from '@/features/messaging/MessageButton';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ErrorNote, InfoNote } from '@/components/Form';
import { WalletIcon } from '@/components/ui/Icons';
import { presentApiError } from '@/lib/form-errors';
import { useDecideBid, useJobBids } from './use-bids';

/**
 * Bids on the employer's jobs. [Job Poster steps 9 and 12]
 *
 * Sorted by price, cheapest first, because that is the question a bid answers.
 * Accepting one assigns the teen, so the copy says so rather than leaving the
 * employer to discover it.
 */

const TONES: Record<string, BadgeTone> = {
  SUBMITTED: 'brand',
  ACCEPTED: 'safe',
  REJECTED: 'neutral',
};

export function JobBidsList({ jobId }: { jobId: string }) {
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const bids = useJobBids({ ...(jobId ? { jobId } : {}), pageSize: 50, sortDir: 'asc' });
  const decide = useDecideBid();

  const items = bids.data?.items ?? [];
  const reputations = useReputations(items.map((bid) => bid.teen.id));

  async function run(action: () => Promise<unknown>, success: string): Promise<void> {
    setError(null);
    setNotice(null);
    try {
      await action();
      setNotice(success);
    } catch (caught) {
      setError(presentApiError(caught, 'Something went wrong').message);
    }
  }

  return (
    <div className="space-y-4">
      {error && <ErrorNote message={error} />}
      {notice && <InfoNote>{notice}</InfoNote>}

      {bids.isPending && (
        <div className="space-y-4" aria-busy="true">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      )}

      {bids.data && items.length === 0 && (
        <EmptyState icon={<WalletIcon className="size-6" />} title="No bids yet">
          {jobId
            ? 'Nobody has bid on this job yet. Bids appear here as they arrive.'
            : 'Bids on your open-budget jobs appear here.'}
        </EmptyState>
      )}

      {items.length > 0 && (
        <ul className="space-y-4">
          {items.map((bid: Bid) => (
            <li
              key={bid.id}
              className="rounded-2xl border border-line bg-white p-5 shadow-soft sm:p-6"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <h2 className="text-base font-bold text-ink">{bid.teen.name}</h2>
                    <ReputationBadge reputation={reputations.data?.[bid.teen.id]} />
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{bid.job.title}</p>
                </div>

                <div className="flex flex-col items-end gap-1">
                  <span
                    className="text-lg font-extrabold text-ink"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatMoney(bid.bidAmount.amountMinor, bid.bidAmount.currency)}
                  </span>
                  <Badge tone={TONES[bid.status] ?? 'neutral'}>
                    {bid.status.charAt(0) + bid.status.slice(1).toLowerCase()}
                  </Badge>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-start gap-2 border-t border-line pt-4">
                <MessageButton jobId={bid.job.id} personId={bid.teen.id} label="Message" />

                {bid.status === 'SUBMITTED' && (
                  <>
                    <Button
                      variant="success"
                      disabled={decide.isPending}
                      onClick={() =>
                        void run(
                          () => decide.mutateAsync({ bidId: bid.id, decision: 'ACCEPT' }),
                          `${bid.teen.name} accepted and assigned at ${formatMoney(
                            bid.bidAmount.amountMinor,
                            bid.bidAmount.currency,
                          )}.`,
                        )
                      }
                    >
                      Accept this bid
                    </Button>
                    <Button
                      variant="ghost"
                      disabled={decide.isPending}
                      onClick={() =>
                        void run(
                          () => decide.mutateAsync({ bidId: bid.id, decision: 'REJECT' }),
                          `${bid.teen.name} declined.`,
                        )
                      }
                    >
                      Decline
                    </Button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
